<template>
  <div class="langModal-container">
    <div
      class="lang-selector"
      v-for="lang in filteredOptionsLang"
      :key="lang.label"
      @click="openModal"
    >
      <div class="selected" v-if="locale === lang.value">
        <img :src="lang.image" :alt="lang.value" />
        <span class="body-4 hidden sm:block" style="cursor: pointer">{{ lang.label2 }}</span>
      </div>
    </div>

    <ModalCustom :isVisible="openLangModal" @close="openLangModal = false">
      <span class="title-langs body-6-bold">{{ $t('menu.selectlenguage') }}</span>

      <div class="lang-list">
        <a
          v-for="lang in filteredOptionsLang"
          :key="lang.label"
          href="#"
          @click.prevent="switchLocale(lang.value)"
          class="lang-link"
        >
          <img :src="lang.image" :alt="lang.label" />
          <span class="body-4">{{ lang.label }}</span>
          <span v-if="locale === lang.value" class="material-symbols-outlined" translate="no">check</span>
        </a>

       <!--  <a href="https://www.tradeviewmena.com/" target="_blank" rel="noopener noreferrer" class="lang-link">
          <img src="/icons/SaudiArabia.svg" alt="Arabic" />
          <span class="body-4" style="font-family: 'Tajawal', sans-serif;">عربي</span>
        </a> -->
      </div>

      <div class="regulations-text">
        <span class="caption-2">{{ $t('regulation.bar.authorized') }}:</span>
        <span class="caption-2"> 
          {{ $t('regulation.bar.europe') }}
        </span>
      </div>
    </ModalCustom>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import ModalCustom from '~/components/utils/modal/ModalCustom.vue'
import { useRouter } from 'vue-router'
import { getAvailableLanguages } from '~/utils/languageConfig.js'
import { useI18n, useSwitchLocalePath } from '#imports'

const { locale, setLocale } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const router = useRouter()

const openLangModal = ref(false)
const openModal = () => { openLangModal.value = true }

// ✅ VARIABLE GLOBAL para evitar conflictos
let isSwitchingLocale = false

const switchLocale = async (newLang) => {
  if (isSwitchingLocale) {
    console.log('🔄 Locale switch already in progress, skipping...')
    return
  }
  
  isSwitchingLocale = true
  
  // ✅ EMITIR EVENTO DE INICIO
  if (process.client) {
    window.dispatchEvent(new CustomEvent('language-switch-start'))
  }
  
  console.log(`🎯 Switch locale started: ${locale.value} → ${newLang}`)
  
  try {
    const currentRoute = router.currentRoute.value
    const currentSlug = currentRoute.params.slug
    const currentCategory = currentRoute.params.categorySlug
    
    // Verificar si estamos en una página de post de Surfs Up
    const isSurfsUpPost = currentSlug && currentCategory && currentRoute.path.includes('/surfs-up/')
    
    if (isSurfsUpPost) {
      console.log('📖 Detected Surfs Up post page, handling specialized translation...')
      await handleSurfsUpPostTranslation(newLang, currentSlug, currentCategory)
    } else {
      console.log('🌐 Normal page, using standard locale switch')
      await handleNormalLocaleSwitch(newLang)
    }
    
    openLangModal.value = false
    console.log('✅ Locale switch completed successfully')
    
  } catch (error) {
    console.error('❌ Error in switchLocale:', error)
    openLangModal.value = false
  } finally {
    // ✅ EMITIR EVENTO DE FINAL después de un delay
    setTimeout(() => {
      isSwitchingLocale = false
      if (process.client) {
        window.dispatchEvent(new CustomEvent('language-switch-end'))
      }
      console.log('🔚 Language switch events emitted')
    }, 800)
  }
}

// ✅ MANEJO DE POSTS SURFS UP - CON NAVEACIÓN DIRECTA
const handleSurfsUpPostTranslation = async (newLang, currentSlug, currentCategory) => {
  try {
    console.log(`🔍 Finding translation for post: ${currentSlug} (${locale.value} → ${newLang})`)
    
    // 1. Buscar el post actual para obtener sus traducciones
    const currentPost = await fetchPostBySlug(currentSlug, locale.value)
    
    if (!currentPost) {
      console.warn('❌ Current post not found, fallback to blog index')
      await handleNormalLocaleSwitch(newLang)
      return
    }
    
    console.log('📄 Current post found:', currentPost.id, currentPost.slug)
    
    // 2. Buscar traducción por ID
    const translationId = currentPost.translations?.[newLang]
    
    if (translationId) {
      console.log(`🎯 Translation ID found: ${translationId}`)
      
      // 3. Buscar el post traducido por ID
      const translatedPost = await fetchPostById(translationId, newLang)
      
      if (translatedPost) {
        console.log('✅ Translated post found:', translatedPost.slug)
        
        // 4. Obtener categoría traducida
        const translatedCategory = await getTranslatedCategorySlug(
          currentCategory, 
          locale.value, 
          newLang, 
          translatedPost
        )
        
        // 5. ✅ NAVEACIÓN DIRECTA SIN PASOS INTERMEDIOS
        const newPath = `/${newLang}/surfs-up/${translatedCategory}/${translatedPost.slug}/`
        console.log(`🔄 Direct navigation to: ${newPath}`)
        
        // ✅ Cambiar locale Y navegar en una sola operación
        await Promise.all([
          setLocale(newLang),
          router.push(newPath)
        ])
        
        return
      }
    }
    
    // 6. Fallback: no hay traducción, ir al índice del blog
    console.warn('⚠️ No translation available, redirecting to blog index')
    const fallbackPath = `/${newLang}/surfs-up/`
    await Promise.all([
      setLocale(newLang),
      router.push(fallbackPath)
    ])
    
  } catch (error) {
    console.error('❌ Error in post translation handling:', error)
    await handleNormalLocaleSwitch(newLang)
  }
}

// ✅ MANEJO NORMAL CON OPERACIÓN ATÓMICA
const handleNormalLocaleSwitch = async (newLang) => {
  const targetPath = switchLocalePath(newLang) || `/${newLang}`
  console.log(`🔀 Normal locale switch to: ${targetPath}`)
  
  // ✅ Operación atómica: cambiar locale y navegar simultáneamente
  await Promise.all([
    setLocale(newLang),
    router.push(targetPath)
  ])
}

// ✅ FUNCIONES AUXILIARES
const fetchPostBySlug = async (slug, lang) => {
  try {
    const { useWordPress } = await import('~/composables/useWordPress')
    const wp = useWordPress()
    const post = await wp.fetchPostBySlug(slug, lang)
    return post || null
  } catch (error) {
    console.error('Error fetching post by slug:', error)
    return null
  }
}

const fetchPostById = async (postId, lang) => {
  try {
    const { useWordPress } = await import('~/composables/useWordPress')
    const wp = useWordPress()
    const post = await wp.fetchPostById(postId, lang)
    return post || null
  } catch (error) {
    console.error('Error fetching post by ID:', error)
    return null
  }
}

const getTranslatedCategorySlug = async (currentCategorySlug, currentLocale, targetLocale, targetPost) => {
  try {
    const { useWordPress } = await import('~/composables/useWordPress')
    const wp = useWordPress()
    
    const [currentCats, targetCats] = await Promise.all([
      wp.fetchCategories(currentLocale),
      wp.fetchCategories(targetLocale)
    ])

    const currentCat = currentCats.find(c => String(c.slug) === String(currentCategorySlug))
    if (!currentCat) {
      console.warn('❌ Current category not found, using original')
      return currentCategorySlug
    }

    const targetCatId = currentCat.translations?.[wp.mapAppToWp(targetLocale)]
    if (targetCatId) {
      const targetCat = targetCats.find(c => Number(c.id) === Number(targetCatId))
      if (targetCat) {
        console.log('✅ Translated category found:', targetCat.slug)
        return targetCat.slug
      }
    }

    // Fallback: primera categoría del post traducido
    if (targetPost?.categories?.length) {
      const fallbackCat = targetCats.find(c => targetPost.categories.includes(c.id))
      if (fallbackCat) {
        console.log('✅ Fallback category from post:', fallbackCat.slug)
        return fallbackCat.slug
      }
    }

    console.warn('⚠️ Using original category as fallback')
    return currentCategorySlug

  } catch (error) {
    console.warn('❌ Error resolving category, using original:', error)
    return currentCategorySlug
  }
}

// ✅ CONFIGURACIÓN DE IDIOMAS
const optionsLang = [
  { label: 'ENG', label2: 'ENG', image: '/icons/UnitedKingdom.svg', alt: 'English', value: 'en' },
  { label: 'ESP', label2: 'ESP', image: '/icons/Spain.svg', alt: 'Español', value: 'es' },
  { label: 'ITA', label2: 'ITA', image: '/icons/Italy.svg', alt: 'Italiano', value: 'it' },
  { label: 'FRA', label2: 'FRA', image: '/icons/France.svg', alt: 'Français', value: 'fr' },
  { label: 'PTG', label2: 'PTG', image: '/icons/Portugal.svg', alt: 'Portuguese', value: 'pt' },
  { label: 'PL', label2: 'PL', image: '/icons/Poland.svg', alt: 'Polski', value: 'pl' },
]

const filteredOptionsLang = computed(() => {
  try {
    const currentPath = router.currentRoute.value.path
    return getAvailableLanguages(currentPath, optionsLang)
  } catch (error) {
    console.warn('Error filtering languages, returning all:', error)
    return optionsLang
  }
})
</script>
